/**
 * Spinner Component
 *
 * Unified loading spinner with size variants.
 * Must use Press.Zone design system classnames so shared SCSS applies.
 *
 * @package MultilingualPressZone
 */

import { el, __ } from '../utils/dom.js';

/**
 * Create a loading spinner element.
 *
 * @param {Object} [options]
 * @param {string} [options.size='md'] - Size variant: sm (14px), md (20px), lg (32px)
 * @param {string} [options.className=''] - Additional CSS classes
 * @param {string} [options.label] - Accessible label for screen readers
 * @returns {HTMLElement}
 */
export function Spinner({ size = 'md', className = '', label } = {}) {
    const sizeClass = size ? `presszone-multilingual-spinner--${size}` : '';

    return el('span', {
        class: ['presszone-multilingual-spinner', sizeClass, className].filter(Boolean).join(' '),
        role: 'status',
        'aria-label': label || __('Loading', 'multilingual-press-zone')
    });
}

/**
 * Create a spinner with optional label text.
 *
 * @param {Object} [options]
 * @param {string} [options.size='md'] - Size variant
 * @param {string} [options.text] - Optional text to display next to spinner
 * @param {string} [options.className=''] - Additional CSS classes
 * @returns {HTMLElement}
 */
export function SpinnerWithText({ size = 'md', text, className = '' } = {}) {
    const container = el('span', {
        class: ['presszone-multilingual-spinner-with-text', className].filter(Boolean).join(' ')
    });

    container.appendChild(Spinner({ size }));

    if (text) {
        container.appendChild(el('span', { class: 'presszone-multilingual-spinner-with-text__label' }, text));
    }

    return container;
}

export default Spinner;
